import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { Plan } from '../../core/models/plan.model';
import { PlanService } from '../../core/services/plan.service';
import { ToastService } from '../../shared/services/toast.service';
import { ExternalCreditCardService } from '../../core/services/external-credit-card.service';
import { UserFacade } from '../../core/store/user/user.facade';
import { User } from '../../core/models/user.model';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import { GetCreditCardIconSrcPipe } from '../../shared/pipes/card-icon-src.pipe';
import { PaymentTextPipe } from '../../shared/pipes/payment-text.pipe';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';

import { PlanPaymentCardComponent } from './plan-payment-card/plan-payment-card.component';

@Component({
  selector: 'umkt-plan-payment-component',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PlanPaymentCardComponent,
    BreadcrumbComponent,
    LoadingComponent,
    ActionButtonComponent, 
    GetCreditCardIconSrcPipe,
    PaymentTextPipe,
    I18nPipe
  ],
  templateUrl: './plan-payment.component.html',
  styleUrl: './plan-payment.component.scss'
})
export class PlanPaymentComponent implements OnInit, OnDestroy {
  @Input() planUuid?: string;
  @Input() embeddedMode = false;

  isLoading = true;
  plan: Plan | null = null;
  breadcrumbItems: BreadcrumbItem[] = [];

  userData: User | null = null;

  selectedCardId: string | null = null;
  initialCardId: string | null = null;
  currentUserId: string | null = null;
  cardType?: string;
  isCardFormVisible = false;
  
  private langSubscription?: Subscription;
  private route = inject(ActivatedRoute);
  private planService = inject(PlanService);
  private location = inject(Location);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private creditCardService = inject(ExternalCreditCardService);
  private userFacade = inject(UserFacade);
  private i18nService = inject(I18nService);


  ngOnInit(): void {
    let planUuid = this.planUuid;

    if (!planUuid && !this.embeddedMode) {
      planUuid = this.route.snapshot.params['planUuid'];
      
      if (!planUuid) {
        planUuid = this.planService.getSelectedPlanId() || '';
      }
    }

    if (!planUuid) {
      if (!this.embeddedMode) {
        this.location.back(); 
      }
      return;
    }
    
    if (!this.embeddedMode) {
      this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
        this.updateBreadcrumb();
      });
      this.updateBreadcrumb();
    }

    this.userFacade.getUserDetails().subscribe(userData => {
      this.userData = userData;
    });

    this.loadPlan(planUuid);
  }

  private updateBreadcrumb(): void {
    this.breadcrumbItems = [
      { label: this.i18nService.instant('shared.labels.myAccount'), url: '/my-account' },
      { label: this.i18nService.instant('shared.labels.plans'), url: `/my-account/plans` },
      { label: this.i18nService.instant('pages.planPayment.breadcrumb.changePayment'), url: '' },
    ];
  }

  onCardFormVisibilityChange(isVisible: boolean): void {
    this.isCardFormVisible = isVisible;
  }

  private loadPlan(planUuid: string): void {
    this.isLoading = true;
    this.planService.getPlanDetailByUuid(planUuid).subscribe({
      next: (resp) => {
        this.plan = resp.data ?? null;
        this.initialCardId = this.plan?.cardToken || null;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Erro ao carregar detalhes do plano para pagamento', err);
        this.isLoading = false;
      },
    });
  }

  onCardSelectionChange(cardId: string): void {
    this.selectedCardId = cardId;
  }

  isSaveButtonEnabled(): boolean {
      return !!this.selectedCardId && this.selectedCardId !== this.initialCardId;
  }

  save(): void {
    this.isLoading = true;
    const payload: any = {
      userUuid: this.userData?.uuid || '',
      token: this.selectedCardId, 
      oldToken: this.initialCardId,
      orderUuid: this.plan?.ordersUuid
    };

    this.creditCardService.updateCreditCard(payload).subscribe({
      next: () => {
        this.toastService.showSuccess(
          this.i18nService.instant('pages.planPayment.messages.success')
        );
        const planUuid = this.plan?.uuid || this.planService.getSelectedPlanId() || '';
        if (planUuid) {
          this.router.navigate(['/my-account/plans/details', planUuid]);
        } else {
          this.router.navigate(['/my-account/plans']);
        }
      },
      error: (err) => {
        console.error('Erro ao atualizar o cartão de crédito', err);
        this.toastService.showError(
          this.i18nService.instant('pages.planPayment.messages.error')
        );
        this.isLoading = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/my-account/plans']);
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}